import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calendar as CalendarIcon, ClipboardCheck, Phone,
  MapPin, Mail, FileText, ShoppingCart, TrendingUp, Users, Building2, AlertTriangle
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, parseISO, subMonths, subDays, startOfYear, endOfDay, startOfDay, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import { getOrderTotal } from '@/lib/orderFields';
import { LEAD_STATUSES, normalizeLeadStatus } from '@/lib/leadStatus';

const COLORS = ['#3b82f6', '#8b5cf6', '#f97316', '#10b981', '#f43f5e', '#06b6d4'];

/** Herhangi bir tarih alanını güvenli parse eder — geçersizse null. */
function safeDate(value) {
  if (!value) return null;
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? d : null;
}

/** Sipariş için en güvenilir tarih: created_date → created_at. */
function orderDate(o) {
  return safeDate(o.created_date) || safeDate(o.created_at);
}

const fmtEUR = (v) =>
  `€${Number(v || 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })}`;

export default function AdminReports() {
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfDay(new Date())
  });
  const [agentFilter, setAgentFilter] = useState('all');

  // Ham listeler tarih filtresiz çekilir; filtreleme client'ta yapılır.
  // Böylece geçersiz/eksik tarihli kayıtlar sorguyu KIRMAZ, sayılıp
  // "Fehlende Daten" kartında raporlanır.
  const { data: allActivities = [] } = useQuery({
    queryKey: ['report-activities'],
    queryFn: () => base44.entities.ActivityReport.list(),
  });
  const { data: allTasks = [] } = useQuery({
    queryKey: ['report-tasks'],
    queryFn: () => base44.entities.Task.list(),
  });
  const { data: allOrders = [] } = useQuery({
    queryKey: ['report-orders'],
    queryFn: () => base44.entities.Order.list(),
  });
  const { data: leads = [] } = useQuery({
    queryKey: ['report-leads'],
    queryFn: () => base44.entities.Lead.list(),
  });

  const inRange = (d) => d && d >= dateRange.from && d <= dateRange.to;

  // Tarih aralığı filtreleri + eksik veri sayaçları
  const { activities, tasks, orders, dataIssues } = useMemo(() => {
    const issues = {
      activitiesNoDate: 0,
      ordersNoDate: 0,
      ordersNoAgent: 0,
      ordersNoValue: 0,
      leadsNoStatus: 0,
    };

    const acts = allActivities.filter((a) => {
      const d = safeDate(a.report_date);
      if (!d) { issues.activitiesNoDate++; return false; }
      return inRange(d);
    });

    const tks = allTasks.filter((t) => {
      const d = safeDate(t.completed_date);
      return d ? inRange(d) : false;
    });

    const ords = allOrders.filter((o) => {
      const d = orderDate(o);
      if (!d) { issues.ordersNoDate++; return false; }
      return inRange(d);
    });

    ords.forEach((o) => {
      if (!o.agent_email) issues.ordersNoAgent++;
      if (!getOrderTotal(o) && !(o.subtotal > 0)) issues.ordersNoValue++;
    });
    issues.leadsNoStatus = leads.filter((l) => !l.status).length;

    return { activities: acts, tasks: tks, orders: ords, dataIssues: issues };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allActivities, allTasks, allOrders, leads, dateRange]);

  const agents = [...new Set([
    ...activities.map(a => a.agent_email),
    ...tasks.map(t => t.assigned_agent),
    ...orders.map(o => o.agent_email)
  ].filter(Boolean))];

  // Filter by agent
  const filteredActivities = agentFilter === 'all' ? activities : activities.filter(a => a.agent_email === agentFilter);
  const filteredTasks = agentFilter === 'all' ? tasks : tasks.filter(t => t.assigned_agent === agentFilter);
  const filteredOrders = agentFilter === 'all' ? orders : orders.filter(o => o.agent_email === agentFilter);

  // Ciro kuralları: iptaller hariç; tutar total → gesamtsumme → subtotal
  const orderRevenue = (o) => getOrderTotal(o) || o.subtotal || 0;
  const validOrders = filteredOrders.filter(o => o.status !== 'Storniert');
  const angebote = validOrders.filter(o => o.order_type === 'Angebot');
  const bestellungen = validOrders.filter(o => o.order_type !== 'Angebot');

  // Stats
  const stats = {
    totalTasks: filteredTasks.length,
    visits: filteredActivities.filter(a => a.activity_type === 'Besuch').length,
    calls: filteredActivities.filter(a => a.activity_type === 'Anruf').length,
    messages: filteredActivities.filter(a => a.activity_type === 'Anschreiben').length,
    proposals: angebote.length,
    ordersCount: bestellungen.length,
    proposalValue: angebote.reduce((s, o) => s + orderRevenue(o), 0),
    orderValue: bestellungen.reduce((s, o) => s + orderRevenue(o), 0),
  };

  // Daily data for chart — saat dilimi güvenli gün eşleştirme
  const days = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
  const dailyData = days.map(day => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const dayActivities = filteredActivities.filter(a => {
      const d = safeDate(a.report_date);
      return d && format(d, 'yyyy-MM-dd') === dayStr;
    });
    const dayOrders = bestellungen.filter(o => {
      const d = orderDate(o);
      return d && format(d, 'yyyy-MM-dd') === dayStr;
    });

    return {
      date: format(day, 'dd.MM'),
      activities: dayActivities.length,
      revenue: Math.round(dayOrders.reduce((s, o) => s + orderRevenue(o), 0)),
    };
  });

  // Agent performance — iptalsiz gerçek siparişler, tutarlı ciro alanı
  const agentPerformance = agents.map(agent => {
    const agentActivities = activities.filter(a => a.agent_email === agent);
    const agentOrders = orders.filter(o =>
      o.agent_email === agent && o.status !== 'Storniert' && o.order_type !== 'Angebot');
    return {
      name: agent.split('@')[0],
      activities: agentActivities.length,
      revenue: Math.round(agentOrders.reduce((s, o) => s + orderRevenue(o), 0)),
    };
  }).sort((a, b) => b.revenue - a.revenue);

  // Lead status distribution — bilinmeyen/eksik statüler de görünür
  const countStatus = (status) => leads.filter(l => normalizeLeadStatus(l.status) === status).length;
  const statusDistribution = [
    { name: 'Potenzielle', value: countStatus('Potenzieller Interessent'), color: '#8b5cf6' },
    { name: 'Interessenten', value: countStatus('Interessenten'), color: '#f59e0b' },
    { name: 'Kunden', value: countStatus('Kunde'), color: '#10b981' },
    { name: 'Verloren', value: countStatus('Verlorene Kunden'), color: '#ef4444' },
    { name: 'eBay', value: countStatus('eBay'), color: '#eab308' },
    { name: 'Shopify', value: countStatus('Shopify'), color: '#22c55e' },
    { name: 'Ohne/Sonstig', value: leads.filter(l => !l.status || !LEAD_STATUSES.includes(normalizeLeadStatus(l.status))).length, color: '#94a3b8' },
  ].filter(s => s.value > 0);

  const issueItems = [
    dataIssues.activitiesNoDate > 0 && `${dataIssues.activitiesNoDate} Aktivität(en) ohne gültiges Datum (nicht im Bericht)`,
    dataIssues.ordersNoDate > 0 && `${dataIssues.ordersNoDate} Auftrag/Aufträge ohne gültiges Datum (nicht im Bericht)`,
    dataIssues.ordersNoAgent > 0 && `${dataIssues.ordersNoAgent} Auftrag/Aufträge ohne Agent-Zuordnung`,
    dataIssues.ordersNoValue > 0 && `${dataIssues.ordersNoValue} Auftrag/Aufträge ohne Betrag (0 €)`,
    dataIssues.leadsNoStatus > 0 && `${dataIssues.leadsNoStatus} Kontakt(e) ohne Status`,
  ].filter(Boolean);

  const statCards = [
    { label: 'Aufgaben erledigt', value: stats.totalTasks, icon: ClipboardCheck, color: 'bg-blue-500' },
    { label: 'Besuche', value: stats.visits, icon: MapPin, color: 'bg-purple-500' },
    { label: 'Anrufe', value: stats.calls, icon: Phone, color: 'bg-cyan-500' },
    { label: 'Nachrichten', value: stats.messages, icon: Mail, color: 'bg-amber-500' },
    { label: 'Angebote', value: stats.proposals, icon: FileText, color: 'bg-orange-500', subtitle: `€${stats.proposalValue.toLocaleString('de-DE', { maximumFractionDigits: 0 })}` },
    { label: 'Bestellungen', value: stats.ordersCount, icon: ShoppingCart, color: 'bg-emerald-500', subtitle: `€${stats.orderValue.toLocaleString('de-DE', { maximumFractionDigits: 0 })}` },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <PageHeader
                title="Berichte"
                subtitle={<>Leistungsübersicht</>}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {/* Quick preset buttons */}
            <div className="flex flex-wrap gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDateRange({
                  from: startOfMonth(new Date()),
                  to: endOfMonth(new Date()),
                })}
              >
                Diesen Monat
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const last = subMonths(new Date(), 1);
                  setDateRange({ from: startOfMonth(last), to: endOfMonth(last) });
                }}
              >
                Letzten Monat
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDateRange({
                  from: startOfDay(subDays(new Date(), 29)),
                  to: endOfDay(new Date()),
                })}
              >
                Letzte 30 Tage
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDateRange({
                  from: startOfYear(new Date()),
                  to: endOfDay(new Date()),
                })}
              >
                Dieses Jahr
              </Button>
            </div>

            {/* Von date picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="justify-start">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Von: {format(dateRange.from, 'dd. MMM yyyy', { locale: de })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateRange.from}
                  onSelect={(date) => {
                    if (!date || !isValid(date)) return;
                    const next = startOfDay(date);
                    setDateRange((prev) => ({
                      from: next,
                      to: prev.to && prev.to >= next ? prev.to : endOfDay(next),
                    }));
                  }}
                />
              </PopoverContent>
            </Popover>

            {/* Bis date picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="justify-start">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Bis: {format(dateRange.to, 'dd. MMM yyyy', { locale: de })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateRange.to}
                  onSelect={(date) => {
                    if (!date || !isValid(date)) return;
                    const next = endOfDay(date);
                    setDateRange((prev) => ({
                      from: prev.from && prev.from <= next ? prev.from : startOfDay(next),
                      to: next,
                    }));
                  }}
                />
              </PopoverContent>
            </Popover>

            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Alle Agenten" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Agenten</SelectItem>
                {agents.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24 space-y-6">
        {/* Fehlende Daten — rapor dışı kalan/eksik kayıtlar şeffaf gösterilir */}
        {issueItems.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/60">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-900">Fehlende / unvollständige Daten</p>
                  <ul className="mt-1 text-xs text-amber-800 space-y-0.5 list-disc list-inside">
                    {issueItems.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((stat, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                    {stat.subtitle && <p className="text-xs text-emerald-600 font-medium">{stat.subtitle}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Activity Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                Aktivitäten & Umsatz
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyData}>
                    <defs>
                      <linearGradient id="colorAct" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} stroke="#94a3b8" allowDecimals={false} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} stroke="#10b981" tickFormatter={fmtEUR} width={70} />
                    <Tooltip formatter={(value, name) => name === 'Umsatz' ? [fmtEUR(value), name] : [value, name]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area yAxisId="left" type="monotone" dataKey="activities" stroke="#3b82f6" fillOpacity={1} fill="url(#colorAct)" name="Aktivitäten" />
                    <Area yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" fillOpacity={1} fill="url(#colorRev)" name="Umsatz" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Lead Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                Lead-Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="w-40 h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={65}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {statusDistribution.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {statusDistribution.map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-sm">{item.name}</span>
                      </div>
                      <span className="font-semibold">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Agent Performance */}
        {agentFilter === 'all' && agentPerformance.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-500" />
                Agent-Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={agentPerformance} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value, name) => name === 'Umsatz' ? [fmtEUR(value), name] : [value, name]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="activities" fill="#3b82f6" name="Aktivitäten" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="revenue" fill="#10b981" name="Umsatz" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
